import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import { shouldShowGold as showGold } from '@iheartjane/menu';
import type { ColorKey } from '@iheartjane/reefer';
import { Image, JaneGoldIcon, Typography } from '@iheartjane/reefer';
import { useInView } from 'react-intersection-observer';

import { BloomLink } from '@jane/bloom/components/bloomLink';
import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import { useCustomLanguage } from '@jane/bloom/hooks/useCustomLanguage';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { trackProductListingTap } from '@jane/bloom/services/tracking/track';
import { getJaneGoldTitle } from '@jane/bloom/utils/gold/getJaneGoldTitle';
import { buildAutocompleteProduct } from '@jane/bloom/viewModels/autocompleteProduct';
import type { AlgoliaProduct } from '@jane/search/types';

import styles from './autocompleteProductCard.module.css';

interface BoldReverseMatchProps {
  className?: string;
  color?: ColorKey;
  matcher: string;
  maxLines?: number | 'none';
  stringToMatch?: string | null;
}

export const BoldReverseMatch = ({
  className,
  color = 'text-light',
  matcher,
  maxLines = 'none',
  stringToMatch,
}: BoldReverseMatchProps) => {
  if (!stringToMatch) return null;
  const sanitized = matcher.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${sanitized})`, 'gi');
  const parts = stringToMatch.split(regex);

  return (
    <Typography
      aria-label={stringToMatch}
      color={color}
      className={className}
      maxLines={maxLines}
    >
      {parts.map((part, index) =>
        regex.test(part) ? <mark key={index}>{part}</mark> : part
      )}
    </Typography>
  );
};

interface AutocompleteProductCardProps {
  hideSearchSuggestions: (persist?: boolean) => void;
  product: SmartSortProduct<AlgoliaProduct>;
  query: string;
}

export const AutocompleteProductCard = ({
  hideSearchSuggestions,
  product,
  query,
}: AutocompleteProductCardProps) => {
  const { ref } = useInView({
    onChange: (inView) => {
      if (inView) {
        product.impress();
      }
    },
  });
  const { featureFlags, goldConfig, pinnedStore, shoppingPreferences } =
    useRootLoaderData();
  const { storeType } = useStoreSwitcher();
  const customLanguage = useCustomLanguage();
  const autocompleteProduct = buildAutocompleteProduct({
    customLanguage,
    product: product.attributes,
    specials: [],
  });
  const {
    baseImage,
    brand,
    imageSrcSet,
    kind,
    lineage,
    name,
    product_id,
    specialLabel,
    unique_slug,
  } = autocompleteProduct;

  const hasJaneGoldLabel = !!getJaneGoldTitle({
    menuProduct: product.attributes,
  });

  const shouldShowGold = Boolean(
    pinnedStore &&
    hasJaneGoldLabel &&
    showGold({
      appMode: 'bloom',
      goldConfig,
      state: pinnedStore.state,
      storeId: pinnedStore.id,
    })
  );

  return (
    <BloomLink
      ref={ref}
      className={styles['autocompleteProductCard']}
      onClick={() => {
        hideSearchSuggestions();
        product.click();
        trackProductListingTap(
          {
            product: autocompleteProduct,
            reservationMode: shoppingPreferences?.reservationMode,
            storeId: pinnedStore.id,
            storeType,
          },
          featureFlags
        );
      }}
      to={`products/${product_id}/${unique_slug ?? product_id}`}
    >
      <div className={styles['autocompleteProductCardImg']}>
        <div className={styles['badgesContainer']}>
          {shouldShowGold && <JaneGoldIcon />}
          {specialLabel && (
            <div className={styles['specialBadge']}>{specialLabel}</div>
          )}
        </div>
        <Image
          src={baseImage}
          altText={name}
          border
          loading="eager"
          objectFit="cover"
          srcSet={imageSrcSet?.srcSet}
          sizes={imageSrcSet?.sizes}
          height="88px"
          width="88px"
          borderRadius="8px"
        />
      </div>
      <div className={styles['autocompleteProductCardDetails']}>
        <BoldReverseMatch
          stringToMatch={name}
          matcher={query}
          color="text-main"
          className={styles['autocompleteProductCardName']}
          maxLines={1}
        />
        <BoldReverseMatch stringToMatch={brand} matcher={query} />
        <BoldReverseMatch
          stringToMatch={`${lineage} ${kind}`}
          matcher={query}
        />
      </div>
    </BloomLink>
  );
};
